import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Zoom,
  Stack,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import moment from "moment";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BedIcon from "@mui/icons-material/Bed";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MoveInPanel from "./MoveInPanel";
import OtsFeePanel from "./OtsFeePanel";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LockIcon from "@mui/icons-material/Lock";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Slider from "react-slick";
import cardPic from "../../../assets/card_pic.webp";
import {
  calculateAdvanceAndRentCommission,
  calculateAdvancePayment,
} from "../../../utils/commissionCalculator";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChatIcon from "@mui/icons-material/Chat";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useSelector, useDispatch } from "react-redux";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Swal from "sweetalert2";
import { isPastDueDate, isDueToday } from "../../../utils/dueDate";
import { allowedForLabel } from "../../../utils/tenantEligibility";
import {
  RequestPaymentExtension,
  CancelInterestRequest,
} from "../../../redux/reducer/FlatInterestRequest";

const RequestCard = ({
  request,
  customRates,
  onOpenPaymentDialog,
  settingsLoading,
  settingsLoaded,
  activeBooking,
  onRefresh,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [openExtensionDialog, setOpenExtensionDialog] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const dispatch = useDispatch();

  // A "hold" request has no commitment behind it — someone else was accepted
  // for this flat, this tenant is just waiting to see if that falls through.
  // They were previously stuck showing "On Hold" forever with no way to back
  // out and go apply elsewhere. Both this and a still-undecided "pending"
  // request can be withdrawn.
  const handleCancelRequest = async () => {
    const confirm = await Swal.fire({
      title: "Cancel this request?",
      text:
        request.status === "hold"
          ? "You'll be removed from this property's waitlist so you can apply to another property."
          : "This request will be withdrawn.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No, keep it",
    });
    if (!confirm.isConfirmed) return;

    setCancelling(true);
    try {
      const result = await dispatch(
        CancelInterestRequest({ requestId: request._id || request.id })
      );
      if (result?.payload?.data?.success || result?.payload?.status === 200) {
        Swal.fire("Cancelled", "Your request has been withdrawn.", "success");
        onRefresh?.();
      } else {
        Swal.fire(
          result?.error?.message || "Failed to cancel the request",
          "",
          "error"
        );
      }
    } catch (err) {
      Swal.fire("Something went wrong. Please try again.", "", "error");
    } finally {
      setCancelling(false);
    }
  };

  /* The tenant already has money on another flat. Paying a second owner on top
     of that is not a choice they meant to make, so this flat's payment stays
     shut until they cancel the booking they hold. */
  const bookedId = activeBooking?._id || activeBooking?.id;
  const blockedByBooking = Boolean(
    bookedId && bookedId !== (request._id || request.id)
  );
  const getStatusColor = (request) => {
    if (request.paymentStatus === "verified") return "success";
    if (request.paymentStatus === "verifying") return "warning";
    if (request.paymentStatus === "failed") return "error";

    const status = request.status;
    switch (status) {
      case "pending":
        return "warning";
      case "accepted":
        return "info";
      case "expired":
        return "error";
      case "hold":
        return "default";
      case "payment_pending":
        return "warning";
      case "completed":
        return "success";
      case "rejected":
      case "payment_rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (request) => {
    if (request.paymentStatus === "verified") return "Payment Verified";
    if (request.paymentStatus === "verifying") return "Verifying Payment";
    if (request.paymentStatus === "failed") return "Payment Failed";

    const status = request.status;
    switch (status) {
      case "pending":
        return "Pending Approval";
      case "accepted":
        return "Accepted - Pay Now";
      case "expired":
        return "Payment Deadline Expired";
      case "hold":
        return "On Hold";
      case "payment_pending":
        return "Verifying Payment";
      case "completed":
        return "Confirmed";
      case "rejected":
        return "Rejected";
      case "payment_rejected":
        return "Payment Rejected";
      default:
        return status;
    }
  };

  const getStatusIcon = (request) => {
    if (request.paymentStatus === "verified") return <CheckCircleIcon />;
    if (request.paymentStatus === "verifying") return <HourglassEmptyIcon />;
    if (request.paymentStatus === "failed") return <CancelIcon />;

    const status = request.status;
    switch (status) {
      case "pending":
        return <HourglassEmptyIcon />;
      case "accepted":
        return <PaymentIcon />;
      case "expired":
        return <ErrorOutlineIcon />;
      case "hold":
        return <HourglassEmptyIcon />;
      case "payment_pending":
        return <ReceiptIcon />;
      case "completed":
        return <CheckCircleIcon />;
      case "rejected":
      case "payment_rejected":
        return <CancelIcon />;
      default:
        return null;
    }
  };

  const CustomArrow = ({ onClick, direction }) => (
    <div
      className={`custom-arrow ${direction}`}
      onClick={onClick}
      role="button"
    />
  );

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    nextArrow: <CustomArrow direction="next" />,
    prevArrow: <CustomArrow direction="prev" />,
  };

  const InfoRow = ({ icon, label, value, important = false }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
      }}
    >
      {icon}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: important ? 600 : 400,
          color: important ? "text.primary" : "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: important ? 700 : 600,
          color: important ? "primary.main" : "text.primary",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Zoom in={true}>
        <Card
          elevation={3}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              transform: "translateY(-8px)",
              boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
            },
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background:
                request.paymentStatus === "verified"
                  ? "linear-gradient(90deg, #4caf50 0%, #388e3c 100%)"
                  : request.paymentStatus === "verifying"
                  ? "linear-gradient(90deg, #ff9800 0%, #f57c00 100%)"
                  : request.status === "accepted"
                  ? "linear-gradient(90deg, #2196f3 0%, #1976d2 100%)"
                  : request.status === "rejected"
                  ? "linear-gradient(90deg, #f44336 0%, #d32f2f 100%)"
                  : "linear-gradient(90deg, #ff9800 0%, #f57c00 100%)",
            },
          }}
        >
          {/* Image Slider */}
          {request?.flatDetail?.images?.length > 0 ? (
            <Box className="flat-slider" sx={{ position: "relative" }}>
              <Slider {...sliderSettings}>
                {request.flatDetail.images.map((img, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={img}
                    alt={`Flat ${request.flatName} ${i}`}
                    sx={{
                      width: "100%",
                      height: 200,
                      objectFit: "cover",
                    }}
                  />
                ))}
              </Slider>
              <Chip
                icon={getStatusIcon(request)}
                label={getStatusLabel(request)}
                color={getStatusColor(request)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}
              />
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={cardPic}
                alt="No flat image"
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  filter: "brightness(0.9)",
                }}
              />
              <Chip
                icon={getStatusIcon(request)}
                label={getStatusLabel(request)}
                color={getStatusColor(request)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}
              />
            </Box>
          )}

          <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 2 }}>
            {/* Property Name & Rent - Most Important */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <HomeIcon color="primary" fontSize="small" />
                <Typography variant="h6" fontWeight={700} noWrap>
                  {request.flatName || "Flat"}
                  {request.flatDetail?.flatNumber
                    ? ` • Flat #${request.flatDetail.flatNumber}`
                    : ""}
                </Typography>
              </Box>

              {/* Where this request actually went — always visible, never
                  behind the expand toggle. flatDetail is the live property
                  record (joined fresh on every fetch), so it has the fuller
                  address; the request's own flatAddress/flatCity (captured
                  once, at send time) is the fallback if the property was
                  since removed. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <LocationOnIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[
                    request.flatDetail?.mapAddress ||
                      request.flatDetail?.propertyAddress ||
                      request.flatAddress,
                    request.flatDetail?.area,
                    request.flatDetail?.city || request.flatCity,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Location not available"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <PersonIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary" noWrap>
                  Owner: {request.landlordName || "N/A"}
                </Typography>
              </Box>

              <Typography variant="h5" color="primary" fontWeight={700}>
                Rs {Number(request.flatRent || 0).toLocaleString()}/mo
              </Typography>
            </Box>

            {/* Key Information - Compact Layout */}
            <Stack spacing={1} sx={{ mb: 2 }}>
              {/* Always rendered (even without flatDetail) so every card in
                  the grid keeps the same number of rows — a missing block
                  here is exactly what made cards look inconsistently sized
                  elsewhere in this app. */}
              <InfoRow
                icon={
                  <BedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                }
                label="Layout"
                value={
                  request.flatDetail
                    ? `${request.flatDetail.flatRooms ?? "N/A"} Bed • ${
                        request.flatDetail.flatToilet ?? "N/A"
                      } Bath • ${request.flatDetail.flatKitchen ?? "N/A"} Kitchen`
                    : "N/A"
                }
              />

              <InfoRow
                icon={
                  <FamilyRestroomIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                }
                label="Allowed For"
                value={
                  allowedForLabel(request.flatDetail?.allowedFor) || "N/A"
                }
              />

              <InfoRow
                icon={
                  <PeopleIcon fontSize="small" sx={{ color: "text.secondary" }} />
                }
                label="Max Occupants Allowed"
                value={
                  request.flatDetail?.personAllowed
                    ? `${request.flatDetail.personAllowed} people`
                    : "N/A"
                }
              />

              <InfoRow
                icon={
                  <AttachMoneyIcon
                    fontSize="small"
                    sx={{ color: "success.main" }}
                  />
                }
                label="Advance Amount"
                value={`Rs ${Number(
                  request.flatAdvance || 0
                ).toLocaleString()}`}
                important
              />

              <InfoRow
                icon={
                  <FileUploadIcon
                    fontSize="small"
                    sx={{ color: "info.main" }}
                  />
                }
                label="Bill Upload By"
                value={
                  request?.flatDetail?.bill_uploaded_by === "landlord"
                    ? "Owner"
                    : "Tenant"
                }
                important
              />

              <InfoRow
                icon={
                  <CalendarMonthIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                }
                label="Move-in Date"
                value={request.moveInDate || "N/A"}
              />
            </Stack>

            {/* Expandable Section */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {request.dueDate && (
                  <InfoRow
                    icon={
                      <CalendarMonthIcon
                        fontSize="small"
                        sx={{ color: "warning.main" }}
                      />
                    }
                    label="Payment Due"
                    value={request.dueDate}
                  />
                )}

                <InfoRow
                  icon={
                    <ReceiptIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  }
                  label="OTS Service Fee"
                  value={`Rs ${calculateAdvanceAndRentCommission(
                    request.flatAdvance || 0,
                    request.flatRent || 0,
                    customRates
                  ).totalCommission.toLocaleString()}`}
                />

                <InfoRow
                  icon={
                    <CalendarMonthIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  }
                  label="Request Submitted"
                  value={moment(request.createdAt).format("DD MMM YYYY")}
                />
              </Stack>

              {/* Owner's Message */}
              {request.responseMessage && (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff8e1",
                    p: 1.5,
                    borderRadius: 2,
                    mb: 2,
                    borderLeft: "3px solid #ff9800",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Owner's Message:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, fontStyle: "italic" }}
                  >
                    "{request.responseMessage}"
                  </Typography>
                </Paper>
              )}
            </Collapse>

            {/* Expand/Collapse Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <IconButton
                type="button"
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                }}
              >
                <Typography variant="caption" sx={{ mr: 0.5 }}>
                  {expanded ? "Show Less" : "Show More"}
                </Typography>
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            {/* Payment Summary - Highlighted */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: "primary.main",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                p: 1.5,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Total Payment Due
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 0.5,
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Rs{" "}
                  {(
                    calculateAdvanceAndRentCommission(
                      request.flatAdvance || 0,
                      request.flatRent || 0,
                      customRates
                    ).totalCommission +
                    (request.flatAdvance || 0) +
                    (request.flatRent || 0)
                  ).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  + Processing Fee
                </Typography>
              </Box>
            </Paper>

            {/* Payment progress.

                This used to be a four-step escrow tracker ending in
                "Released" — shown to every tenant who paid, cash included.
                Two things were wrong with that. Escrow language only makes
                sense when the money actually went through Alfa or SafePay and
                an escrow record exists; a tenant who handed over cash was
                being told their payment was "held in escrow" and later
                "released", neither of which happened. And "Released" is an
                OTS-to-owner event — for the tenant the milestone that matters
                is that their booking is confirmed, so that is what the third
                step says now.

                The move-in is the fourth step. It belongs on the same track:
                the tenant was reading "Booking confirmed" as an unfinished
                step and a separate blue panel underneath as unrelated news.
                One line now runs from paying to living there, and the panel
                below is simply where the buttons for the last step live. */}
            {(request.paymentStatus === "verifying" ||
              request.paymentStatus === "verified") &&
              request.moveInStatus !== "cancelled" &&
              (() => {
                const method = String(request.paymentMethod || "").toLowerCase();
                const isOnline = method === "alfa" || method === "safepay";
                // Only true when a real escrow record exists behind it.
                const inEscrow = request.escrowStatus === "held";
                const verified = request.paymentStatus === "verified";

                // Bookings made before move-in confirmation existed carry no
                // moveInStatus. Those tenants are already living there, so an
                // unfinished "Moved in" step would be wrong for them.
                const tracksMoveIn = request.moveInStatus === "awaiting";
                const movedIn = verified && !tracksMoveIn;

                // MUI marks every step *before* activeStep as complete, so the
                // finished state is one past the last index — three steps done
                // is activeStep 3, all four done is 4.
                const activeStep = movedIn ? 4 : verified ? 3 : 1;

                return (
                  <Paper
                    elevation={0}
                    sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, mb: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      {isOnline ? (
                        <LockIcon color="primary" fontSize="small" />
                      ) : (
                        <ReceiptIcon color="primary" fontSize="small" />
                      )}
                      <Typography variant="subtitle2" fontWeight={600}>
                        Payment Status
                      </Typography>
                    </Box>

                    <Stepper
                      activeStep={activeStep}
                      alternativeLabel
                      sx={{ mb: 2 }}
                    >
                      <Step>
                        <StepLabel>
                          {isOnline ? "Paid" : "Receipt sent"}
                        </StepLabel>
                      </Step>
                      <Step>
                        <StepLabel>Owner verifies</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel>Booking confirmed</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel>Moved in</StepLabel>
                      </Step>
                    </Stepper>

                    {verified ? (
                      <Alert
                        severity="success"
                        icon={<CheckCircleIcon />}
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {tracksMoveIn
                          ? "Payment verified — your booking is confirmed. One step left: moving in."
                          : "Payment verified by the owner. Your booking is confirmed."}
                      </Alert>
                    ) : inEscrow ? (
                      <Alert
                        severity="info"
                        icon={<AccountBalanceWalletIcon />}
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Held securely by OTS. The owner receives nothing until
                        your payment is verified.
                      </Alert>
                    ) : (
                      <Alert
                        severity="warning"
                        icon={<HourglassEmptyIcon />}
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {isOnline
                          ? "Payment received. Waiting for the owner to verify."
                          : "Receipt submitted. Waiting for the owner to verify it."}
                      </Alert>
                    )}
                  </Paper>
                );
              })()}

            {/* Cash bookings: the separate OTS service-fee receipt */}
            <OtsFeePanel request={request} onChanged={onRefresh} />

            {/* Booked, waiting to actually move in */}
            <MoveInPanel request={request} onChanged={onRefresh} />

            {/* Action Buttons */}

            {(request.status === "accepted" || request.status === "expired") &&
              (request.paymentStatus === "unpaid" ||
                request.paymentStatus === "failed") &&
              (blockedByBooking ? (
                <Alert
                  severity="info"
                  icon={<LockIcon />}
                  sx={{ borderRadius: 2, fontSize: "0.85rem" }}
                >
                  <strong>You already have a booking in progress.</strong>
                  <br />
                  Your payment for{" "}
                  <strong>{activeBooking.flatName || "another property"}</strong>{" "}
                  is holding that flat. To take this one instead, cancel that
                  booking first — then this payment opens up.
                </Alert>
              ) : isPastDueDate(request.dueDate) &&
                request.paymentStatus === "unpaid" ? (
                <>
                  <Alert
                    severity="error"
                    sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}
                  >
                    <strong>Payment Deadline Expired.</strong>
                    <br />
                    Your payment deadline has passed. Please wait for the
                    owner's decision
                    {request.extensionStatus === "none" &&
                      " or request a payment extension"}
                    .
                  </Alert>

                  {request.extensionStatus === "none" ? (
                    <Button
                      variant="contained"
                      color="warning"
                      type="button"
                      fullWidth
                      onClick={() => setOpenExtensionDialog(true)}
                      sx={{
                        py: 1.2,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                        },
                      }}
                    >
                      Request Extension
                    </Button>
                  ) : (
                    <Alert
                      severity="info"
                      icon={<HourglassEmptyIcon />}
                      sx={{ borderRadius: 2, fontSize: "0.85rem" }}
                    >
                      Extension requested — waiting for the owner's response.
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  {isDueToday(request.dueDate) && (
                    <Alert
                      severity="warning"
                      icon={<AccessTimeIcon />}
                      sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}
                    >
                      <strong>Today is your payment due date.</strong>
                      <br />
                      Pay before midnight to keep this booking.
                    </Alert>
                  )}

                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<PaymentIcon />}
                    onClick={() => onOpenPaymentDialog(request)}
                    disabled={
                      !settingsLoaded || settingsLoading || blockedByBooking
                    }
                    sx={{
                      py: 1.2,
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                      "&:hover": {
                        boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                      },
                    }}
                  >
                    {settingsLoading ? "Loading..." : "Make Payment"}
                  </Button>
                </>
              ))}

            {/* Status Messages */}
            {request.status === "completed" && !request.paymentStatus && (
              <Alert
                severity="success"
                sx={{ borderRadius: 2, fontSize: "0.85rem" }}
              >
                Booking confirmed! Contact owner for next steps.
              </Alert>
            )}

            {(request.status === "rejected" ||
              request.status === "payment_rejected") && (
              <Alert
                severity="error"
                sx={{ borderRadius: 2, fontSize: "0.85rem" }}
              >
                {request.status === "payment_rejected"
                  ? "Your payment was rejected. Please contact owner."
                  : "Your request was rejected."}
              </Alert>
            )}

            {request.status === "hold" && (
              <Alert
                severity="info"
                icon={<HourglassEmptyIcon />}
                sx={{ borderRadius: 2, fontSize: "0.85rem" }}
              >
                Another applicant is currently being processed for this flat.
                If they don't complete payment in time, your request will
                become active again.
              </Alert>
            )}

            {(request.status === "pending" || request.status === "hold") && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={handleCancelRequest}
                disabled={cancelling}
                sx={{ mt: 1.5, borderRadius: 2, textTransform: "none" }}
              >
                {cancelling ? "Cancelling..." : "Cancel Request"}
              </Button>
            )}
          </CardContent>
        </Card>
      </Zoom>
      <Dialog
        open={openExtensionDialog}
        onClose={() => setOpenExtensionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 2 }}>
          {/* Warning Icon */}
          <Box
            sx={{
              width: 70,
              height: 70,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              bgcolor: "#FFF3E0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 40, color: "#F57C00" }} />
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            Request Payment Extension
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your payment deadline has already expired.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              bgcolor: "#FFF8E1",
              border: "1px solid #FFE082",
              borderRadius: 2,
              p: 2,
              textAlign: "left",
              mb: 3,
            }}
          >
            <Typography variant="body2">
              📌 <strong>Important:</strong>
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              • The owner may approve or reject your request.
            </Typography>

            <Typography variant="body2">
              • If approved, the extension will be for a maximum of{" "}
              <strong>2 days</strong>.
            </Typography>

            <Typography variant="body2">
              • No additional extension can be requested.
            </Typography>
          </Paper>

          <Typography variant="body1" fontWeight={500}>
            Do you want to send the extension request?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenExtensionDialog(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            variant="contained"
            color="warning"
            onClick={async () => {
              const result = await dispatch(
                RequestPaymentExtension({
                  requestId: request._id || request.id,
                  tenantId: request.tenantId,
                })
              );
              setOpenExtensionDialog(false);

              if (RequestPaymentExtension.fulfilled.match(result)) {
                Swal.fire(
                  "Extension requested",
                  "The owner has been notified.",
                  "success"
                );
                onRefresh?.();
              } else {
                Swal.fire(
                  "Couldn't send request",
                  result.payload?.message || "Please try again.",
                  "error"
                );
              }
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestCard;
